import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of as observableOf } from 'rxjs';
import { map, skipWhile, switchMap } from 'rxjs/operators';

import { AccountService } from '@vendasta/business-center';
import { ApiService } from '../api-service/api.service';
import { FileGroup } from './filegroup';
import { ProductService } from './product.service';
import { Product } from './product/product';

@Injectable({ providedIn: 'root' })
export class FileGroupService {
  cursorSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private cursorValue: string;
  fileGroupSubject: BehaviorSubject<FileGroup[]> = new BehaviorSubject<FileGroup[]>(null);
  accountGroupId: string;
  partnerId: string;

  constructor(
    private apiService: ApiService,
    private productService: ProductService,
    private businessService: AccountService,
  ) {}

  get cursor(): Observable<string> {
    return this.cursorSubject.asObservable();
  }

  getFileGroups(accountGroupId: string, partnerId: string): Observable<FileGroup[]> {
    this.cursorSubject.next(null);
    this.cursorValue = null;
    this.fileGroupSubject.next(null);

    const url = `/_ajax/v1/account/files/?accountGroupId=${accountGroupId}&partnerId=${partnerId}`;
    this.partnerId = partnerId;
    this.accountGroupId = accountGroupId;

    this.loadFileGroups(url).subscribe((fileGroups) => {
      this.cursorSubject.next(this.cursorValue);
      this.fileGroupSubject.next(fileGroups);
    });

    return this.fileGroupSubject.asObservable();
  }

  getMoreFileGroups(): Observable<FileGroup[]> {
    const url = `/_ajax/v1/account/files/?accountGroupId=${this.accountGroupId}&partnerId=${
      this.partnerId
    }&cursor=${this.cursorSubject.getValue()}`;

    this.loadFileGroups(url).subscribe((fileGroups) => {
      this.cursorSubject.next(this.cursorValue);
      this.fileGroupSubject.next([...this.fileGroupSubject.getValue(), ...fileGroups]);
    });

    return this.fileGroupSubject.asObservable();
  }

  private loadFileGroups(url: string): Observable<FileGroup[]> {
    return this.apiService.get(url).pipe(
      switchMap((resp) => {
        const fileGroups = resp.feed_data.map((item) => {
          this.cursorValue = resp.cursor;
          return new FileGroup(item);
        });
        const fileGroups$ = observableOf(fileGroups);
        const product$ = this.productService.products;
        return combineLatest([fileGroups$, product$]);
      }),
      skipWhile(([, products]) => !products),
      map(([fileGroups, products]) => {
        if (products.length > 0) {
          return this.setUploadedByFromProducts(fileGroups, products);
        } else {
          return null;
        }
      }),
    );
  }

  setUploadedByFromProducts(fileGroups: FileGroup[], products: Product[]): FileGroup[] {
    return fileGroups.map((fileGroup) => {
      if (fileGroup.app_id === 'AA') {
        fileGroup.uploadedBy = 'Partner Center';
      } else {
        const product = products.find((thisProduct) => fileGroup.app_id === thisProduct.product_id);
        fileGroup.uploadedBy = product ? product.name : 'Vendor Center';
      }
      return fileGroup;
    });
  }

  deleteFileGroup(fileGroup: FileGroup): Observable<void> {
    return this.businessService.deleteFile(fileGroup.file_group_id).pipe(
      map((_) => {
        this.fileGroupSubject.next([
          ...this.fileGroupSubject.getValue().filter((fg) => fg.file_group_id !== fileGroup.file_group_id),
        ]);
        return;
      }),
    );
  }
}
